import { z } from "zod";
//#region src/app-token.ts
/**
* The app-token exchange (feature 2026-09-18-audience-bound-dp-token, D-1, D-15).
*
* A signed-in principal holds one session token, which core-api mints with no
* per-app claims from stage two of that feature. When the shell opens a hosted
* app it posts the app's id here with the session bearer and receives an
* access token bound to that one app: `appAccess.appIds` of one, `scp` as the
* keys the principal holds on that app, `appPermissions` as that app's tuples
* only, and an `aud` of `[issuer, appId, appSlug]` (R-009, R-051). The shell
* hands hosted apps a `fetch` bound to that token; the session token never
* reaches an app's own routes again.
*
* The route is authenticated and admits one token class by positive test
* (`security-audit.md` chain C1, R-018): a first-party human session token,
* with `scope: 'data_plane'`, `principalKind` absent or `'human'`, `exp` and
* `iat` present, and none of `scp`, `azp`, `chainDepth` or `sessionType`, on a
* request that carries no `X-Zion-On-Behalf-Of` header. Every other caller is
* refused, each with the same 403 body. `APP_TOKEN_REFUSAL_CODES` in
* core-api's `AppTokenService` carries the whole list, ten codes:
* `onBehalfOf`, `mcpSession`, `machinePrincipal`, `delegatedCaller` (the
* unscoped run-as mint included, whose only marker is `chainDepth`, R-037),
* `notFirstPartySession`, `noExpiry`, `appTokenCaller` (a verified `aud` that
* is not the issuer string, so an app token cannot buy another, R-051,
* R-055), `spentSession`, `appNotVisible` and `passwordChangeRequired` (the
* principal's user row holds `force_password_change`, log code
* `app_token_password_change_required`, #1172 D4). An app token is minted from a
* session and never from another narrowed or long-lived token. The reason is
* a log-only code (#2880 D-005).
*/
const APP_TOKEN_ROUTE = "/v1/auth/app-token";
/**
* The request body: the catalog id of the app to bind to. Strict, so a second
* key is a 400 through `ZionStrictValidationPipe`. The id is the
* `release.apps_catalog` UUID, the same value `AppPermissionClaim.appId`
* carries and `ResolvedApp.appUuid` exposes on `GET /v1/apps`.
*/
const appTokenRequestSchema = z.object({ appId: z.string().uuid().describe("The release.apps_catalog id of the app to bind the token to.") }).strict();
/**
* The response: the compact RS256 access token, its lifetime in seconds, and
* the app id it is bound to, echoed so a client that raced two exchanges can
* tell which one answered. `expiresIn` is at most the session token's
* remaining lifetime, and never more than `ACCESS_TOKEN_TTL` (D-5). There is
* no refresh token: an app token is re-minted from the session token and is
* never refreshed on its own.
*/
const appTokenResponseSchema = z.object({
	accessToken: z.string().min(1).describe("The compact RS256 access token bound to appId."),
	expiresIn: z.number().int().positive().describe("Seconds until the token expires; at most the session token's remaining lifetime."),
	appId: z.string().uuid().describe("The app the token is bound to; equals the request's appId.")
}).strict();
//#endregion
export { APP_TOKEN_ROUTE, appTokenRequestSchema, appTokenResponseSchema };

//# sourceMappingURL=app-token.js.map